// src/App.js
import React, { Suspense } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import { Toaster } from 'react-hot-toast';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { LocationProvider } from './context/LocationContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';
import { SocketProvider } from './context/SocketContext';

// Components
import AppRoutes from './routes';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorFallback from './components/common/ErrorFallback';
import ErrorBoundary from './components/common/ErrorBoundary';

// Styles
import './styles/index.css';
import 'leaflet/dist/leaflet.css';


// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)
    },
    mutations: {
      retry: 1,
    },
  },
});

// Error fallback component
const AppErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <ErrorFallback 
      error={error} 
      resetErrorBoundary={resetErrorBoundary}
      title="Application Error"
      message="Something went wrong with the application. Please try refreshing the page."
    />
  );
};

// Loading component for Suspense
const AppLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <LoadingSpinner size="large" />
  </div>
);

function App() {
  return (
    <ErrorBoundary
      FallbackComponent={AppErrorFallback}
      onReset={() => window.location.reload()}
    >
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <Router>
            <ThemeProvider>
              <AuthProvider>
                <LocationProvider>
                  <NotificationProvider>
                    <SocketProvider>
                      <div className="min-h-screen bg-gray-50">
                        <Suspense fallback={<AppLoadingFallback />}>
                          <AppRoutes />
                        </Suspense>
                        
                        {/* Toast Notifications */}
                        <Toaster 
                          position="top-right"
                          toastOptions={{
                            duration: 4000,
                            style: {
                              background: '#363636',
                              color: '#fff',
                            },
                            success: {
                              duration: 3000,
                              theme: {
                                primary: '#10B981',
                                secondary: '#059669',
                              },
                            },
                            error: {
                              duration: 5000,
                              theme: {
                                primary: '#EF4444',
                                secondary: '#DC2626',
                              },
                            },
                          }}
                        />
                      </div>
                    </SocketProvider>
                  </NotificationProvider>
                </LocationProvider>
              </AuthProvider>
            </ThemeProvider>
          </Router>
          
          {/* React Query Devtools - Only in development */}
          {process.env.NODE_ENV === 'development' && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;